<template>
  <div>
    <Navbar @showHome="$router.push('/')" @showSignin="$router.push({ path: '/', query: { signin: 'true' } })" @showSignup="$router.push({ path: '/', query: { signup: 'true' } })" @showFeedback="showFeedbackModal" />
    
    <!-- GiveFeedback modal -->
    <GiveFeedback v-if="isFeedbackModalVisible" @close="closeFeedbackModal" />
    
    <div class="container mt-5 utah-draw-info">
    <div class="row">
      <div class="col-lg-10 offset-lg-1">
        <h1 class="text-center mb-4">Utah Draw Odds Information</h1>
        
        <!-- Introduction Section -->
        <section id="introduction" class="mb-5">
          <h2>Introduction to Utah's Draw System</h2>
          <p>
            Utah's hunting draw system allocates limited hunting permits through a preference and bonus point system. 
            Understanding this system is crucial for hunters looking to maximize their chances of drawing coveted tags for 
            deer, elk, pronghorn, bison, and other species in Utah.
          </p>
          <p>
            Each year, thousands of hunters apply for various hunting opportunities in Utah, but only a limited number of 
            permits are available. The Utah Division of Wildlife Resources (DWR) uses a sophisticated draw system to 
            distribute these permits fairly while rewarding loyalty to specific hunts.
          </p>
          <p>
            For most hunters, tracking their points and understanding their draw odds can be challenging. That's where 
            HuntApply.com helps – we provide accurate draw odds calculations and help you track your points in one convenient 
            location.
          </p>
        </section>

        <!-- General Season Deer Section -->
        <section id="general-season-deer" class="mb-5">
          <h2>General Season Deer Draw Odds</h2>
          <p>
            Utah's general season deer hunts are allocated through a preference point system. Here's how it works:
          </p>
          <ul>
            <li><strong>Preference Points:</strong> You earn one preference point each time you apply for a general-season deer permit and are unsuccessful.</li>
            <li><strong>Point System:</strong> Applicants with the most preference points draw first. When there's a tie in points, a random draw occurs among those applicants.</li>
            <li><strong>Point Usage:</strong> Once you successfully draw a permit, all your preference points are zeroed out.</li>
            <li><strong>Application Period:</strong> Applications are typically accepted in January and February each year.</li>
          </ul>
          <p>
            For general season deer hunts, draw odds vary significantly by unit. Some popular units might require 2-3 points to draw reliably, 
            while others may be drawnable with 0-1 points. Youth hunters receive preference in the general deer drawing, with 20% of permits 
            reserved for hunters under 18.
          </p>
          <p>
            Our draw odds calculator takes into account historical draw rates, current point creep trends, and unit-specific demand 
            to provide you with accurate odds for successfully drawing your desired hunt.
          </p>
        </section>

        <!-- Limited Entry Hunts Section -->
        <section id="limited-entry-hunts" class="mb-5">
          <h2>Limited Entry Hunts</h2>
          <p>
            Limited entry hunts in Utah offer premiere hunting opportunities with controlled access and typically higher trophy quality. 
            These hunts use a bonus point system rather than preference points.
          </p>
          
          <h3>Elk Limited Entry</h3>
          <p>
            Utah's limited entry elk hunts are among the most competitive in the state, with some units requiring 15+ bonus points for a high 
            probability of drawing. The bonus point system works as follows:
          </p>
          <ul>
            <li><strong>Bonus Points:</strong> You earn one bonus point each time you're unsuccessful in the draw.</li>
            <li><strong>Draw Odds:</strong> Your chance of drawing increases with more bonus points. For each point you have, you receive an additional entry in the draw.</li>
            <li><strong>Point Retention:</strong> Unlike preference points, you do not lose all your bonus points when you draw a tag. You only lose the points for that specific species.</li>
            <li><strong>50/50 Split:</strong> 50% of limited entry permits go to applicants with the most points, while 50% go to a random draw where having more points gives you more chances.</li>
          </ul>

          <h3>Pronghorn Limited Entry</h3>
          <p>
            Pronghorn hunts also use the bonus point system. These hunts typically require fewer points than premium elk units but are still 
            highly competitive. Most desirable units may require 8-12 bonus points for a high probability draw.
          </p>

          <h3>Bison Limited Entry</h3>
          <p>
            Bison hunts are among the most difficult tags to draw in Utah. With very few permits available and extremely high demand, 
            some hunters wait decades for a bison opportunity. These hunts also use the bonus point system, with some hunters accumulating 
            20+ points before drawing.
          </p>

          <h3>Other Species</h3>
          <p>
            Utah also offers limited entry hunts for desert bighorn sheep, Rocky Mountain bighorn sheep, mountain goat, moose, and 
            once-in-a-lifetime hunts. These all operate on the bonus point system, with varying levels of difficulty to draw 
            depending on the species and unit.
          </p>
        </section>

        <!-- Factors Affecting Draw Odds Section -->
        <section id="factors-affecting-odds" class="mb-5">
          <h2>Factors Affecting Draw Odds</h2>
          <p>
            Several factors influence your chances of drawing a tag in Utah:
          </p>
          <ol>
            <li><strong>Unit Popularity:</strong> More applicants for a specific unit means lower draw odds.</li>
            <li><strong>Hunt Season:</strong> Early seasons (archery, early rifle) often have higher demand than late seasons.</li>
            <li><strong>Permit Numbers:</strong> The number of available permits directly affects odds.</li>
            <li><strong>Point Creep:</strong> The gradual increase in points needed to draw a tag as more hunters enter the system and retain points.</li>
            <li><strong>Resident vs. Non-Resident:</strong> Non-residents have a 10% allocation cap, making their odds significantly lower for popular hunts.</li>
          </ol>
        </section>

        <!-- FAQ Section -->
        <section id="faq" class="mb-5">
          <h2>Frequently Asked Questions</h2>

          <div class="card mb-3">
            <div class="card-header">
              <h3 class="h5 mb-0">What's the difference between preference points and bonus points?</h3>
            </div>
            <div class="card-body">
              <p>
                <strong>Preference Points</strong> (used for general season deer) work on a top-down system where hunters with the most points draw first, 
                and all points are lost upon a successful draw.
              </p>
              <p>
                <strong>Bonus Points</strong> (used for limited entry and once-in-a-lifetime hunts) give you additional entries in the draw for each point you have. 
                You only lose your points for a specific species when you successfully draw that species.
              </p>
            </div>
          </div>

          <div class="card mb-3">
            <div class="card-header">
              <h3 class="h5 mb-0">How do I check my current Utah draw points?</h3>
            </div>
            <div class="card-body">
              <p>
                You can check your current Utah preference and bonus points through:
              </p>
              <ol>
                <li>The <a href="https://wildlife.utah.gov" target="_blank">Utah Division of Wildlife Resources website</a></li>
                <li>Creating a free account on HuntApply.com and using our point tracking system</li>
              </ol>
              <p>
                HuntApply.com allows you to sync and track your points year over year, making it easier to plan your applications.
              </p>
            </div>
          </div>

          <div class="card mb-3">
            <div class="card-header">
              <h3 class="h5 mb-0">Can I earn a preference/bonus point without applying for a tag?</h3>
            </div>
            <div class="card-body">
              <p>
                Yes, Utah offers a "point only" application option. During the regular application period, you can apply just for a 
                point without being entered into the draw for a tag. This is a popular strategy for hunters who know they can't hunt in a 
                particular year but want to keep building points.
              </p>
            </div>
          </div>

          <div class="card mb-3">
            <div class="card-header">
              <h3 class="h5 mb-0">What happens if I forget to apply for a point?</h3>
            </div>
            <div class="card-body">
              <p>
                If you miss an application period, you will not earn a point for that year. Points are not automatically accumulated - 
                you must apply either for a hunt or a "point only" application each year to earn a point. There is no way to make up for 
                missed points from previous years.
              </p>
            </div>
          </div>

          <div class="card mb-3">
            <div class="card-header">
              <h3 class="h5 mb-0">How accurate are draw odds predictions?</h3>
            </div>
            <div class="card-body">
              <p>
                Draw odds calculations are estimates based on historical data, current point distributions, and trend analysis. While no 
                prediction can be 100% accurate due to variables like changing application numbers and permit quotas, HuntApply.com's draw 
                odds calculator provides highly accurate estimates by analyzing comprehensive data from previous draws.
              </p>
            </div>
          </div>

          <div class="card mb-3">
            <div class="card-header">
              <h3 class="h5 mb-0">What is point creep and why does it matter?</h3>
            </div>
            <div class="card-body">
              <p>
                Point creep refers to the gradual increase in the number of points needed to draw a particular hunt over time. It occurs when:
              </p>
              <ul>
                <li>More hunters enter the system each year than leave it</li>
                <li>Hunters accumulate and hold points for longer periods</li>
                <li>Permit numbers remain static or decrease</li>
              </ul>
              <p>
                Understanding point creep helps you make strategic application decisions, particularly whether to apply for hunts that might 
                become unreachable in the future if the creep continues.
              </p>
            </div>
          </div>
        </section>

        <!-- Strategic Application Advice Section -->
        <section id="strategy" class="mb-5">
          <h2>Strategic Application Advice</h2>
          <p>
            Developing an effective application strategy can significantly improve your hunting opportunities over time:
          </p>
          <ul>
            <li><strong>Set realistic goals:</strong> Understand which hunts are achievable with your current points and which are long-term goals.</li>
            <li><strong>Consider point banking:</strong> Sometimes it's strategic to pass on marginal opportunities to build points for premium hunts.</li>
            <li><strong>Look for overlooked opportunities:</strong> Some units offer comparable hunting experiences with much better draw odds.</li>
            <li><strong>Utilize group applications:</strong> For some hunts, group applications can optimize your drawing chances.</li>
            <li><strong>Apply for points consistently:</strong> Never miss an application period, even if you're applying for points only.</li>
          </ul>
          <p>
            HuntApply.com's odds calculator takes the guesswork out of this process, allowing you to make data-driven decisions for your application strategy.
          </p>
        </section>

        <!-- Call to Action -->
        <section id="cta" class="mb-5 text-center">
          <div class="card bg-light p-4">
            <h2>Ready to Improve Your Draw Success?</h2>
            <p class="lead">
              Create a free account on HuntApply.com to track your points, calculate your draw odds, and plan your hunting strategy.
            </p>
          <div>
            <a href="#" class="btn btn-primary btn-lg me-2" @click="navigateToSignUp">Sign Up Free</a>
            <a href="#" class="btn btn-outline-primary btn-lg" @click="navigateToSignIn">Sign In</a>
          </div>
          </div>
        </section>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import Navbar from './Navbar.vue';
import GiveFeedback from './GiveFeedback.vue';

export default {
  name: 'UtahDrawInfo',
  components: {
    Navbar,
    GiveFeedback
  },
  data() {
    return {
      isFeedbackModalVisible: false
    };
  },
  
  // Set custom title and meta tags when component is created for SEO
  metaInfo: {
    title: 'Utah Draw Odds Information & FAQ | HuntApply',
    meta: [
      { name: 'description', content: 'Learn about Utah draw odds for deer, elk, pronghorn, bison and other species. Comprehensive guide to Utah\'s hunting application system.' },
      { name: 'keywords', content: 'Utah draw odds, deer draw odds, elk draw odds, limited entry hunts, preference points, bonus points, Utah hunting' }
    ]
  },
  
  methods: {
    navigateToSignUp() {
      // Navigate to home with signup query parameter - this will be handled in AppEncapsulated's created hook
      this.$router.push({ path: '/', query: { signup: 'true' } });
    },
    navigateToSignIn() {
      // Navigate to home with signin query parameter - this will be handled in AppEncapsulated's created hook
      this.$router.push({ path: '/', query: { signin: 'true' } });
    },
    showFeedbackModal() {
      this.isFeedbackModalVisible = true;
    },
    closeFeedbackModal() {
      this.isFeedbackModalVisible = false;
    }
  },

  mounted() {
    // Update document title and meta tags for SEO
    document.title = "Utah Draw Odds Information & FAQ | HuntApply";
    
    // Update description meta tag
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", 
        "Complete guide to Utah draw odds for deer, elk, pronghorn, and bison hunts. Learn how preference points, bonus points, and the Utah draw system works."
      );
    }
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }
};
</script>

<style scoped>
.utah-draw-info h1 {
  color: #2c3e50;
  margin-bottom: 2rem;
}

.utah-draw-info h2 {
  color: #2c3e50;
  margin-top: 2rem;
  margin-bottom: 1.2rem;
  border-bottom: 2px solid #f1f1f1;
  padding-bottom: 0.5rem;
}

.utah-draw-info h3 {
  color: #3498db;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.utah-draw-info p, .utah-draw-info li {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #34495e;
}

.utah-draw-info ul, .utah-draw-info ol {
  padding-left: 1.5rem;
  margin-bottom: 1.5rem;
}

.utah-draw-info li {
  margin-bottom: 0.5rem;
}

.utah-draw-info .card {
  border: 1px solid rgba(0,0,0,.125);
  border-radius: 0.25rem;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}

.utah-draw-info .card-header {
  background-color: rgba(0,0,0,.03);
  border-bottom: 1px solid rgba(0,0,0,.125);
  padding: 0.75rem 1.25rem;
}

.utah-draw-info .card-body {
  padding: 1.25rem;
}

.utah-draw-info .card-header h3 {
  margin: 0;
  color: #2c3e50;
}

/* Ensure responsive behavior */
@media (max-width: 768px) {
  .utah-draw-info p, .utah-draw-info li {
    font-size: 1rem;
  }
  
  .utah-draw-info h1 {
    font-size: 2rem;
  }
  
  .utah-draw-info h2 {
    font-size: 1.6rem;
  }
  
  .utah-draw-info h3 {
    font-size: 1.3rem;
  }
}
</style>
